import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Space,
    Typography,
    Card,
    message,
    Popconfirm,
    Row,
    Col,
    Tag,
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    getRespondentsApi,
    createRespondentApi,
    updateRespondentApi,
    deleteRespondentApi,
} from "../../../../api/master.api";

const { Title } = Typography;

const RespondentList = () => {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // 🔥 Fetch Respondents
    const fetchData = useCallback(async (page = 1, limit = 10, search = "") => {
        setLoading(true);
        try {
            const { data } = await getRespondentsApi(page, limit, search);
            setItems(data.data.items || []);
            setPagination({
                current: data.data.pagination.page,
                pageSize: data.data.pagination.limit,
                total: data.data.pagination.total,
            });
        } catch (error) {
            message.error("Failed to load respondents");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(1, pagination.pageSize, searchText);
    }, [fetchData, pagination.pageSize, searchText]);

    // 🔥 Create
    const handleCreate = async (values) => {
        try {
            await createRespondentApi(values);
            message.success("Respondent created successfully");
            setCreateOpen(false);
            createForm.resetFields();
            fetchData(1, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Create failed");
            console.error(error);
        }
    };

    // 🔥 Update
    const handleUpdate = async (values) => {
        try {
            await updateRespondentApi(selected.respondent_id, values);
            message.success("Respondent updated successfully");
            setEditOpen(false);
            editForm.resetFields();
            fetchData(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Update failed");
            console.error(error);
        }
    };

    // 🔥 Delete
    const handleDelete = async (id) => {
        try {
            await deleteRespondentApi(id);
            message.success("Respondent deleted successfully");
            fetchData(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Delete failed");
            console.error(error);
        }
    };

    // 🔥 Table Columns
    const columns = [
        {
            title: "ID",
            dataIndex: "respondent_id",
            width: 80,
            key: "respondent_id",
        },
        {
            title: "Respondent List",
            dataIndex: "respondent_name",
            key: "respondent_name",
            ellipsis: true,
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelected(record);
                            editForm.setFieldsValue({
                                respondent_name: record.respondent_name,
                            });
                            setEditOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Delete this respondent?"
                        description="Are you sure you want to delete this?"
                        onConfirm={() => handleDelete(record.respondent_id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Row justify="space-between" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4}>রেসপন্ডেন্ট তালিকা (Respondent List)</Title>
                    <Tag color="blue" style={{ marginTop: 5 }}>
                        Total: {pagination.total}
                    </Tag>
                </Col>
                <Col>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchData(pagination.current, pagination.pageSize, searchText)}
                        >
                            Refresh
                        </Button>
                        <Input.Search
                            placeholder="Search respondent..."
                            allowClear
                            onSearch={(value) => {
                                setSearchText(value);
                                fetchData(1, pagination.pageSize, value);
                            }}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    setSearchText("");
                                    fetchData(1, pagination.pageSize, "");
                                }
                            }}
                            enterButton={<SearchOutlined />}
                            style={{ width: 250 }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateOpen(true)}
                        >
                            Add New Respondent
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="respondent_id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        onChange: (page, pageSize) => {
                            fetchData(page, pageSize, searchText);
                        },
                        onShowSizeChange: (current, size) => {
                            fetchData(1, size, searchText);
                        },
                    }}
                    scroll={{ x: 768 }}
                />
            </Card>

            {/* CREATE MODAL */}
            <Modal
                title="Add New Respondent"
                open={createOpen}
                onCancel={() => {
                    setCreateOpen(false);
                    createForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={createForm}
                    onFinish={handleCreate}
                    layout="vertical"
                    initialValues={{ respondent_name: "" }}
                >
                    <Form.Item
                        name="respondent_name"
                        label="Respondent Name"
                        rules={[
                            { required: true, message: "Please enter respondent name" },
                            { min: 2, message: "Minimum 2 characters required" },
                        ]}
                    >
                        <Input
                            placeholder="Enter respondent name"
                            size="large"
                            autoFocus
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setCreateOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Create
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* EDIT MODAL */}
            <Modal
                title="Edit Respondent"
                open={editOpen}
                onCancel={() => {
                    setEditOpen(false);
                    editForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={editForm}
                    onFinish={handleUpdate}
                    layout="vertical"
                >
                    <Form.Item
                        name="respondent_name"
                        label="Respondent Name"
                        rules={[
                            { required: true, message: "Please enter respondent name" },
                            { min: 2, message: "Minimum 2 characters required" },
                        ]}
                    >
                        <Input
                            placeholder="Enter respondent name"
                            size="large"
                            autoFocus
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setEditOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Update
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RespondentList;